import * as Sentry from '@sentry/browser';
import { config, getSiteModule } from 'env_def';
import { api } from '../../../api/api';
import { apis, environment, inWindow } from '../../../config';
import { TransConf } from '../../../api/data/errorReport';
import { toJson } from '../../../api/util';
const isDev = process.env.NODE_ENV === 'development' || config('stage') === 'dev' || config('stage') === 'local';
function isL2cError(sentryEvent) {
    var _a;
    try {
        const values = (_a = sentryEvent.exception) === null || _a === void 0 ? void 0 : _a.values;
        if (values) {
            return values.some(value => {
                var _a;
                const stackFrames = (_a = value.stacktrace) === null || _a === void 0 ? void 0 : _a.frames;
                if (stackFrames) {
                    return stackFrames.some(stackFrame => {
                        var _a;
                        if ((_a = stackFrame.filename) === null || _a === void 0 ? void 0 : _a.includes('/9590deda-')) {
                            return true;
                        }
                    });
                }
            });
        }
    }
    catch (error) { }
    return false;
}
class ErrorReport {
    constructor() {
        this.name = apis.errorReportImpl;
        this.systemApi = api.getSystemApi();
        this.dataStore = api.getDataStoreApi();
        this.userInfo = this.initUserInfo();
        this.loggerApi = api.requireLogicalApi(apis.loggerApiImpl);
        this.inited = false;
        this.transMap = new Map();
    }
    init() {
        this.userInfo = this.initUserInfo();
        return this.name;
    }
    initUserInfo() {
        return {
            id: '',
            lastLoginTime: 0,
            nickName: '',
            uuid: this.dataStore && this.dataStore.getUUID ? this.dataStore.getUUID() : '',
        };
    }
    getModuleName() {
        if (!inWindow()) {
            return '';
        }
        const otherMatch = window.location.hash.match(/^#(\w+)/);
        const path = window.location.pathname || 'unknown';
        return otherMatch ? otherMatch[1] : path;
    }
    initSentry() {
        var _a;
        const isElectron = this.systemApi.isElectron();
        const currentUser = this.systemApi.getCurrentUser();
        const user = currentUser || this.initUserInfo();
        this.userInfo = {
            id: user.id,
            lastLoginTime: user.lastLoginTime,
            nickName: (currentUser === null || currentUser === void 0 ? void 0 : currentUser.sessionId) || '_',
            uuid: this.dataStore.getUUID(),
        };
        const dsn = isElectron ? this.systemApi.getUrl('electronSentry') : this.systemApi.getUrl('webSentry');
        const env = typeof environment === 'string' ? environment : 'local';
        let sentryExtraParams = {};
        const version = config('version');
        const isTest = ['prev', 'test'].includes(env);
        const otherModule = this.getModuleName();
        sentryExtraParams = {
            sampleRate: isTest ? 1 : 0.02,
            tracesSampleRate: isTest ? 1 : 0.02,
            maxValueLength: 1000,
            beforeSend(event) {
                event.tags = event.tags || {};
                if (!event.tags.moduleName) {
                    event.tags.moduleName = isL2cError(event) ? 'l2c-crm' : otherModule;
                }
                return event;
            },
            initialScope: {
                user: {
                    id: (_a = this.userInfo) === null || _a === void 0 ? void 0 : _a.id,
                },
                tags: {
                    version,
                },
            },
            ignoreErrors: [
                'ResizeObserver loop limit exceeded',
                'The database connection is closing',
                'UnknownError: Internal error opening backing store for indexedDB.open.',
                '非持久化消息需要有消费者才可发送',
                'BG_WIN_NOT_EXIST',
                'ERR.LOGIN.SMSSESSEXP',
                'Minified React error',
                'login status not proper',
                'Failed to fetch',
            ],
        };
        if (!this.inited && !isDev) {
            const release = window.electronLib && window.electronLib.env ? window.electronLib.env.showVersion : config('version');
            const moduleName = this.getModuleName();
            const options = Object.assign({ beforeSend: (event) => event, dsn, environment: env, denyUrls: [/https?:\/\/localhost/gi], integrations: [
                    new Sentry.BrowserTracing({
                        beforeNavigate(context) {
                            if (!inWindow()) {
                                return context;
                            }
                            const module = getSiteModule(window.location.href);
                            context.name = module && module.url ? module.url : moduleName;
                            const newTags = {
                                nav_type: 'hashChange',
                                module_name: module && module.module_name ? module.module_name : 'unknown',
                                sub_module_name: module && module.sub_module_name ? module.sub_module_name : 'unknown',
                                not_in_site_module: !!module,
                            };
                            context.tags = context.tags ? Object.assign(Object.assign({}, context.tags), newTags) : newTags;
                            return context;
                        },
                    }),
                ], release, autoSessionTracking: true, dist: env }, sentryExtraParams);
            Sentry.init(options);
            this.inited = true;
            console.log('sentry init success', options);
            Sentry.configureScope(scope => {
                scope.setExtra('errorInfo', Object.assign({}, this.userInfo));
                return scope;
            });
        }
    }
    afterInit() {
        this.initSentry();
        return this.name;
    }
    afterLogin() {
        this.initSentry();
        return this.name;
    }
    beforeLogout() {
        this.userInfo = this.initUserInfo();
        return this.name;
    }
    doReportMessage(error, optionalInfo) {
        var _a;
        if (!error) {
            return;
        }
        const env = config('stage');
        if (env !== 'local') {
            if (optionalInfo) {
                const combineMessage = Object.assign(Object.assign({}, this.userInfo), { optionalInfo });
                const errorInfo = Object.assign({}, combineMessage);
                Sentry.configureScope(scope => {
                    scope.setExtra('errorInfo', errorInfo);
                    return scope;
                });
            }
            if (error instanceof Error) {
                const eid = Sentry.captureException(error);
                console.log('error reported with eid:' + eid);
            }
            else {
                const message = `${(_a = this.userInfo) === null || _a === void 0 ? void 0 : _a.uuid}的错误:${error ? toJson(error) : 'unknown'}`;
                Sentry.captureMessage(message, 'warning');
            }
        }
    }
    endTransaction(conf) {
        const { id } = conf;
        const tid = typeof id === 'number' ? id : parseInt(id || '0');
        const transaction = this.transMap.get(tid);
        if (transaction) {
            transaction.sampled = true;
            this.setTagAndData(conf, transaction);
            const rtid = transaction.finish();
            this.transMap.delete(tid);
            this.loggerApi.track('perf_transaction_finished', { conf, rtid });
            if (rtid) {
                console.warn('transaction finished with tid:' + tid);
            }
        }
    }
    startTransaction(conf) {
        const { name, op, parent } = conf;
        try {
            const id = this.getUniqueID();
            const parentTrans = parent && this.transMap.get(parent);
            let transaction;
            if (TransConf[name]) {
                const { sampleRate } = TransConf[name];
                const sampled = sampleRate && sampleRate > 0 && Math.random() < sampleRate;
                if (!sampled)
                    return -2;
            }
            const context = {
                name,
                op: op || 'default',
                sampled: true,
                tags: {
                    moduleName: this.getModuleName(),
                },
            };
            if (parentTrans) {
                transaction = parentTrans.startChild(context);
            }
            else {
                transaction = Sentry.startTransaction(context);
                if (!conf.notAsTop && transaction)
                    Sentry.configureScope(scope => scope.setSpan(transaction));
            }
            this.checkTransSize();
            if (transaction) {
                this.setTagAndData(conf, transaction);
                this.transMap.set(id, transaction);
                this.loggerApi.track('perf_transaction_start', { conf, id });
                return id;
            }
            return -3;
        }
        catch (e) {
            console.error('[err] error when start sentry transaction:', e);
            return -1;
        }
    }
    checkTransSize() {
        if (this.transMap.size > 35) {
            this.transMap.clear();
        }
    }
    setTagAndData(conf, transaction) {
        console.log('setTagAndData', conf);
        if (conf && conf.tags) {
            Object.entries(conf.tags).forEach(([k, v]) => {
                transaction.setTag(k, v);
            });
        }
        if (conf && conf.data) {
            Object.entries(conf.data).forEach(([k, v]) => {
                transaction.setData(k, v);
            });
        }
    }
    getTransById(id) {
        const tid = typeof id === 'number' ? id : parseInt(id || '0');
        return this.transMap.get(tid);
    }
    getUniqueID() {
        return Date.now() * 1000 + Math.floor(Math.random() * 1000);
    }
}
const errorReportImpl = new ErrorReport();
api.registerLogicalApi(errorReportImpl);
export default errorReportImpl;
